/**
 * WhatsApp Templates Page — shadcn/ui redesign
 *
 * Shell: page header + shadcn Tabs (Message Templates / Automated Sequences).
 * All channel-fetching logic preserved exactly.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Inbox, MessageSquare, Layers } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import TemplateManagement from '../components/WhatsApp/TemplateManagement';
import SequenceManagement from '../components/WhatsApp/SequenceManagement';
import api from '../services/api';

interface WhatsAppInbox {
  id: number;
  account_id: number;
  channel_type: string;
  channel_id?: number;
  whatsapp_channel_id?: number;
}

const WhatsAppTemplatesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('templates');
  const [loading, setLoading] = useState(true);
  const [channelId, setChannelId] = useState<number | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);

  const navigate = useNavigate();

  // Fetch WhatsApp channel for this account — logic unchanged
  useEffect(() => {
    const fetchWhatsAppChannel = async () => {
      setLoading(true);
      try {
        const response = await api.get('/inboxes/');
        const responseData = response.data;
        const inboxes: WhatsAppInbox[] = responseData?.inboxes || responseData || [];

        if (Array.isArray(inboxes)) {
          const whatsappInbox = inboxes.find(
            (inbox) => inbox.channel_type?.toLowerCase() === 'whatsapp',
          );

          if (whatsappInbox) {
            setChannelId(whatsappInbox.id);
            setAccountId(whatsappInbox.account_id);
          }
        }
      } catch {
        // silent — no-channel state is shown below
      } finally {
        setLoading(false);
      }
    };

    fetchWhatsAppChannel();
  }, []);

  // ── Loading skeleton ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-4 w-[440px] max-w-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-44 rounded-lg" />
          <Skeleton className="h-9 w-48 rounded-lg" />
        </div>
        <Skeleton className="h-[480px] w-full rounded-lg" />
      </div>
    );
  }

  // ── No-channel empty state ───────────────────────────────────────────────────
  if (!channelId || !accountId) {
    return (
      <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-20">
          <div className="rounded-full bg-muted p-4">
            <Phone className="h-8 w-8 text-muted-foreground" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold">No WhatsApp channel configured</p>
            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
              Set up a WhatsApp inbox to manage message templates and automated sequences.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/inboxes')}>
            <Inbox className="mr-2 h-3.5 w-3.5" />
            Go to Inboxes
          </Button>
        </div>
      </div>
    );
  }

  // ── Main page ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-1 flex-col gap-4 py-4 px-4 md:gap-6 md:py-6 lg:px-6">

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          WhatsApp Templates &amp; Sequences
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage message templates and automated sequences for marketing and customer engagement
        </p>
      </div>

      {/* Content tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-4">
        <TabsList className="w-fit">
          <TabsTrigger value="templates" className="gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" />
            Message Templates
          </TabsTrigger>
          <TabsTrigger value="sequences" className="gap-1.5">
            <Layers className="h-3.5 w-3.5" />
            Automated Sequences
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          <TemplateManagement accountId={accountId} channelId={channelId} />
        </TabsContent>

        <TabsContent value="sequences">
          <SequenceManagement accountId={accountId} />
        </TabsContent>
      </Tabs>

    </div>
  );
};

export default WhatsAppTemplatesPage;
